import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faUpload, faEdit, faToggleOn, faToggleOff,
  faSpinner, faTags, faSearch, faSync, faGlobe, faTimes,
  faFilter, faChevronDown, faTrash
} from '@fortawesome/free-solid-svg-icons';
import Layout from '../../components/Layout';
import Footer from '../../components/ui/Footer';
import CSVUploadModal from '../../components/Invoicing/CSVUploadModal';
import TradeOpsDeleteModal from '../../components/TradeOpsDeleteModal';
import HMSBadge from '../../components/ui/HMSBadge';
import HMSButton from '../../components/ui/HMSButton';
import HMSEmptyState from '../../components/ui/HMSEmptyState';
import HMSPageHeader from '../../components/ui/HMSPageHeader';
import HMSSkeleton from '../../components/ui/HMSSkeleton';
import {
  fetchCostCategories, createCostCategory, updateCostCategory, deleteCostCategory,
  uploadCostCategoriesCSV,
} from '../../services/invoicingService';
import { useFlashMessage } from '../../components/FlashMessage';
import { useAuth } from '../../context/AuthContext';
import './OutgoingInvoice.css';
import '../../components/Invoicing/InvoiceSearchFilter.css';

const TEMPLATE_COLUMNS = [
  'Group', 'Group Label', 'Sub Group', 'Sub Group Label',
  'Code', 'Abacus Code', 'Short Name', 'Description', 'Description EN',
];

const CostCategoriesPage = () => {
  const history = useHistory();
  const { user } = useAuth();
  const isFinancialApprover = user?.is_financial_approver || user?.is_superuser;
  const { showError, showSuccess } = useFlashMessage();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const [filters, setFilters] = useState({ is_active: '', group: '', sub_group: '' });
  const [showFilters, setShowFilters] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteWarning, setDeleteWarning] = useState(false);
  const [deleteWarningMsg, setDeleteWarningMsg] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const toggleDropdown = (id) => {
    setActiveDropdown(activeDropdown === id ? null : id);
  };

  const activeFilters = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
  const activeFiltersCount = Object.keys(activeFilters).length;

  const loadCategories = useCallback(async (page = 1, append = false) => {
    if (append) { setLoadingMore(true); } else { setLoading(true); }
    try {
      const data = await fetchCostCategories({ show_inactive: 'true' }, {
        page, search: debouncedSearch || undefined,
        filters: activeFiltersCount > 0 ? activeFilters : undefined,
      });
      const items = data.results || data || [];
      if (append) {
        setCategories(prev => [...prev, ...items]);
      } else {
        setCategories(items);
      }
      setTotalCount(data.count ?? items.length);
      setHasMore(!!data.next);
      setCurrentPage(page);
    } catch (err) {
      console.error('Failed to load cost categories:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [debouncedSearch, activeFiltersCount]);

  useEffect(() => { loadCategories(1, false); }, [loadCategories]);

  const handleLoadMore = () => { loadCategories(currentPage + 1, true); };

  const openCreateModal = () => {
    setEditingCategory(null);
    setFormData({
      group: '', group_label: '', sub_group: '', sub_group_label: '',
      code: '', abacus_code: '', short_name: '',
      description: '', description_en: '',
    });
    setShowCreateModal(true);
  };

  const openEditModal = (cat) => {
    setEditingCategory(cat);
    setFormData({
      group: cat.group, group_label: cat.group_label || '',
      sub_group: cat.sub_group, sub_group_label: cat.sub_group_label || '',
      code: cat.code, abacus_code: cat.abacus_code || '',
      short_name: cat.short_name,
      description: cat.description || '', description_en: cat.description_en || '',
    });
    setShowCreateModal(true);
  };

  const handleSave = async () => {
    if (!formData.group || !formData.sub_group || !formData.code || !formData.short_name) {
      showError('Group, Sub Group, Code, and Short Name are required.');
      return;
    }
    setSaving(true);
    try {
      if (editingCategory) {
        await updateCostCategory(editingCategory.id, formData);
        showSuccess('Cost category updated successfully.');
      } else {
        await createCostCategory(formData);
        showSuccess('Cost category created successfully.');
      }
      setShowCreateModal(false);
      loadCategories(1, false);
    } catch (err) {
      const detail = err.response?.data;
      if (detail && typeof detail === 'object') {
        showError(Object.values(detail).flat().join(', '));
      } else {
        showError('Failed to save cost category.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (cat) => {
    try {
      await updateCostCategory(cat.id, { is_active: !cat.is_active });
      loadCategories(1, false);
    } catch (err) {
      console.error('Toggle failed:', err);
    }
  };

  const handleUpload = async (file) => await uploadCostCategoriesCSV(file);

  const handleUploadClose = () => {
    setShowUploadModal(false);
    loadCategories(1, false);
  };

  const openDeleteModal = (cat) => {
    setDeleteTarget(cat);
    setDeleteWarning(false);
    setDeleteWarningMsg('');
    setShowDeleteModal(true);
    setActiveDropdown(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setIsDeleting(true);
      await deleteCostCategory(deleteTarget.id);
      showSuccess('Cost category permanently deleted.');
      setShowDeleteModal(false);
      setDeleteTarget(null);
      loadCategories(1, false);
    } catch (err) {
      const data = err.response?.data;
      if (err.response?.status === 409 && data?.blockers) {
        const lines = data.blockers.map(b => b.type).join(', ');
        setDeleteWarningMsg(`${data.message}\n\nReferenced by: ${lines}`);
        setDeleteWarning(true);
      } else if (err.response?.status === 403) {
        setDeleteWarningMsg(data?.error || 'Permission denied.');
        setDeleteWarning(true);
      } else {
        showError(data?.error || 'Delete failed.');
        setShowDeleteModal(false);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const uniqueGroups = [...new Set(categories.map(c => c.group))].sort();
  const uniqueSubGroups = [...new Set(
    categories.filter(c => !filters.group || c.group === filters.group).map(c => c.sub_group).filter(Boolean)
  )].sort();

  const groupedCategories = {};
  categories.forEach(c => {
    if (!groupedCategories[c.group]) groupedCategories[c.group] = [];
    groupedCategories[c.group].push(c);
  });

  return (
    <Layout showLeftNavSidebar={true} showRightSidebar={false}>
      <div className="invoice-page-container master-data-screen cost-categories-screen">
        <HMSPageHeader
          onBack={() => history.push('/invoicing')}
          title="Cost Categories"
          kicker="Finance"
          actions={
            <>
              <button className="btn-secondary" onClick={() => setShowUploadModal(true)}>
                <FontAwesomeIcon icon={faUpload} /> Upload CSV
              </button>
              <button className="btn-primary" onClick={openCreateModal}>
                <FontAwesomeIcon icon={faPlus} /> Create Category
              </button>
            </>
          }
        />

        <div className="search-filter-controls">
          <span className="master-data-scope-badge">
            <FontAwesomeIcon icon={faGlobe} /> Global scope
          </span>
          <div className="search-filter-wrapper">
            <div className="invoice-search-filter">
              <div className="search-header">
                <div className="search-input-group">
                  <div className="search-input-container">
                    <FontAwesomeIcon icon={faSearch} className="search-icon" />
                    <input
                      type="text"
                      placeholder="Search by group, sub group, code, name, or description..."
                      value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                      className="search-input compact"
                    />
                    {searchTerm && (
                      <button className="clear-search" onClick={() => setSearchTerm('')}>
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    )}
                  </div>
                  <button
                    className={`filter-toggle-button ${showFilters ? 'active' : ''}`}
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <FontAwesomeIcon icon={faFilter} />
                    Filters
                    {activeFiltersCount > 0 && <span className="filter-count">{activeFiltersCount}</span>}
                    <FontAwesomeIcon icon={faChevronDown} className={`chevron ${showFilters ? 'rotated' : ''}`} />
                  </button>
                </div>
              </div>
              {showFilters && (
                <div className="search-filters-panel">
                  <div className="filters-grid">
                    <div className="form-group">
                      <label>Status</label>
                      <select value={filters.is_active} onChange={(e) => setFilters(f => ({ ...f, is_active: e.target.value }))} className="form-select">
                        <option value="">All</option>
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Group</label>
                      <select value={filters.group} onChange={(e) => setFilters(f => ({ ...f, group: e.target.value, sub_group: '' }))} className="form-select">
                        <option value="">All Groups</option>
                        {uniqueGroups.map(g => <option key={g} value={g}>{g}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Sub Group</label>
                      <select value={filters.sub_group} onChange={(e) => setFilters(f => ({ ...f, sub_group: e.target.value }))} className="form-select">
                        <option value="">All Sub Groups</option>
                        {uniqueSubGroups.map(sg => <option key={sg} value={sg}>{sg}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="filters-actions">
                    <button className="btn btn-primary btn-sm" onClick={() => { loadCategories(1, false); setShowFilters(false); }}>Apply</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => { setFilters({ is_active: '', group: '', sub_group: '' }); setSearchTerm(''); }} disabled={activeFiltersCount === 0 && !searchTerm}>Clear All</button>
                  </div>
                </div>
              )}
            </div>
          </div>
          <button className="reload-button" onClick={() => loadCategories(1, false)} title="Reload categories">
            <FontAwesomeIcon icon={faSync} />
          </button>
        </div>

        <div className="invoice-list-container">
          {loading ? (
            /* Skeleton in the shape of the category table that is coming.
               `.loading-state` keeps the padding the spinner block used; only
               its centring has to go, or the bars collapse to their content
               width. */
            <div className="loading-state" style={{ alignItems: 'stretch' }}>
              <HMSSkeleton shape="title" />
              <HMSSkeleton lines={6} />
            </div>
          ) : categories.length === 0 ? (
            /* `card` because the block this replaces drew its own white panel
               around the centred text. */
            <HMSEmptyState
              variant="card"
              icon={<FontAwesomeIcon icon={faTags} />}
              title={searchTerm ? 'No Results' : 'No Cost Categories'}
              hint={searchTerm ? 'No categories match your search' : 'No cost categories found. Upload a CSV or create manually.'}
            />
          ) : (
            <>
            <table className="invoice-list-table">
              <thead>
                <tr>
                  <th>Group</th>
                  <th>Sub Group</th>
                  <th>Code</th>
                  <th>Abacus</th>
                  <th>Short Name</th>
                  <th>Description (DE / EN)</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(groupedCategories).sort().map(group => (
                  groupedCategories[group].map((cat, idx) => (
                    <tr key={cat.id}>
                      {idx === 0 ? (
                        <td rowSpan={groupedCategories[group].length} className="invoice-number-cell" style={{ verticalAlign: 'top' }}>
                          {group}
                          {cat.group_label && (
                            <div style={{ fontSize: '0.75em', color: '#888', fontWeight: 400, marginTop: 2 }}>
                              {cat.group_label}
                            </div>
                          )}
                        </td>
                      ) : null}
                      <td>
                        {cat.sub_group}
                        {cat.sub_group_label && (
                          <div style={{ fontSize: '0.75em', color: '#888', marginTop: 2 }}>
                            {cat.sub_group_label}
                          </div>
                        )}
                      </td>
                      <td style={{ fontFamily: 'monospace', fontWeight: 500 }}>{cat.code}</td>
                      <td style={{ fontFamily: 'monospace', color: '#666' }}>{cat.abacus_code || '-'}</td>
                      <td>{cat.short_name}</td>
                      <td style={{ maxWidth: 320 }} title={[cat.description, cat.description_en].filter(Boolean).join('\n\n')}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {cat.description || '-'}
                        </div>
                        {cat.description_en && (
                          <div style={{ fontSize: '0.8em', color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            EN: {cat.description_en}
                          </div>
                        )}
                      </td>
                      <td>
                        <HMSBadge status={cat.is_active ? 'approved' : 'draft'}>
                          {cat.is_active ? 'Active' : 'Inactive'}
                        </HMSBadge>
                      </td>
                      <td className="actions-cell">
                        <div
                          className={`action-dropdown ${activeDropdown === cat.id ? 'dropdown-open' : ''}`}
                          onClick={(e) => e.stopPropagation()}
                          ref={activeDropdown === cat.id ? dropdownRef : null}
                        >
                          <button
                            className="gear-button"
                            onClick={(e) => { e.stopPropagation(); toggleDropdown(cat.id); }}
                          >
                            Actions
                          </button>
                          {activeDropdown === cat.id && (
                            <div className="dropdown-menu">
                              <button onClick={() => { setActiveDropdown(null); openEditModal(cat); }}>
                                <FontAwesomeIcon icon={faEdit} /> Edit
                              </button>
                              <hr />
                              <button onClick={() => { setActiveDropdown(null); handleToggleActive(cat); }}>
                                <FontAwesomeIcon icon={cat.is_active ? faToggleOff : faToggleOn} />
                                {cat.is_active ? ' Deactivate' : ' Activate'}
                              </button>
                              {isFinancialApprover && (
                                <>
                                  <hr />
                                  <button className="delete-action" onClick={() => openDeleteModal(cat)}>
                                    <FontAwesomeIcon icon={faTrash} /> Delete
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ))}
              </tbody>
            </table>
            <div className="master-data-load-more">
              <span className="load-more-count">
                Showing {categories.length.toLocaleString()} of {totalCount.toLocaleString()}
              </span>
              {hasMore && (
                <HMSButton variant="ghost" size="compact" onClick={handleLoadMore} disabled={loadingMore}>
                  {loadingMore ? <><FontAwesomeIcon icon={faSpinner} spin /> Loading...</> : 'Show 100 more'}
                </HMSButton>
              )}
            </div>
            </>
          )}
        </div>

        <Footer />
      </div>

      {showCreateModal && (
        <div className="md-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="md-modal" onClick={(e) => e.stopPropagation()}>
            <div className="md-modal-header">
              <div className="md-modal-header-text">
                <FontAwesomeIcon icon={editingCategory ? faEdit : faPlus} className="md-modal-header-icon" />
                <h2>{editingCategory ? 'Edit Cost Category' : 'Create Cost Category'}</h2>
              </div>
              <button className="md-modal-close" onClick={() => setShowCreateModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="md-modal-body">
              <div className="md-form-group">
                <label className="md-form-label">Group <span className="required">*</span></label>
                <input className="md-form-input" type="text" value={formData.group} onChange={(e) => setFormData({ ...formData, group: e.target.value })} placeholder="e.g. People, Travel, IT" list="group-suggestions" />
                <datalist id="group-suggestions">
                  {uniqueGroups.map(g => <option key={g} value={g} />)}
                </datalist>
              </div>
              <div className="md-form-group">
                <label className="md-form-label">Group Label (long form)</label>
                <input className="md-form-input" type="text" value={formData.group_label || ''} onChange={(e) => setFormData({ ...formData, group_label: e.target.value })} placeholder="e.g. Gross Operating Profit" />
              </div>
              <div className="md-form-group">
                <label className="md-form-label">Sub Group <span className="required">*</span></label>
                <input className="md-form-input" type="text" value={formData.sub_group} onChange={(e) => setFormData({ ...formData, sub_group: e.target.value })} placeholder="e.g. Salaries, Software" />
              </div>
              <div className="md-form-group">
                <label className="md-form-label">Sub Group Label (long form)</label>
                <input className="md-form-input" type="text" value={formData.sub_group_label || ''} onChange={(e) => setFormData({ ...formData, sub_group_label: e.target.value })} placeholder="e.g. Revenue" />
              </div>
              <div className="md-form-group">
                <label className="md-form-label">Code <span className="required">*</span></label>
                <input className="md-form-input" type="text" value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value })} placeholder="e.g. IT-SW, TRVL-AIR, A1005" />
              </div>
              <div className="md-form-group">
                <label className="md-form-label">Abacus Code</label>
                <input className="md-form-input" type="text" value={formData.abacus_code || ''} onChange={(e) => setFormData({ ...formData, abacus_code: e.target.value })} placeholder="e.g. 1001" />
              </div>
              <div className="md-form-group">
                <label className="md-form-label">Short Name <span className="required">*</span></label>
                <input className="md-form-input" type="text" value={formData.short_name} onChange={(e) => setFormData({ ...formData, short_name: e.target.value })} placeholder="Short display label" />
              </div>
              <div className="md-form-group">
                <label className="md-form-label">Description (DE)</label>
                <input className="md-form-input" type="text" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="What belongs in this category" />
              </div>
              <div className="md-form-group">
                <label className="md-form-label">Description (EN)</label>
                <input className="md-form-input" type="text" value={formData.description_en || ''} onChange={(e) => setFormData({ ...formData, description_en: e.target.value })} placeholder="English description (optional)" />
              </div>
            </div>
            <div className="md-modal-footer">
              <button className="md-btn-cancel" onClick={() => setShowCreateModal(false)}>Cancel</button>
              <button className="md-btn-submit" onClick={handleSave} disabled={saving}>
                {saving ? <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</> : editingCategory ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      <CSVUploadModal
        isOpen={showUploadModal} onClose={handleUploadClose} onUpload={handleUpload}
        title="Upload Cost Categories" templateColumns={TEMPLATE_COLUMNS}
        entityRequired={false}
      />

      <TradeOpsDeleteModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setDeleteTarget(null); }}
        onConfirm={handleDelete}
        itemType="master_data"
        itemDetails={deleteTarget ? {
          recordType: 'Cost Category',
          recordCode: deleteTarget.code,
          recordName: deleteTarget.short_name,
        } : {}}
        isWarning={deleteWarning}
        warningMessage={deleteWarningMsg}
        isDeleting={isDeleting}
      />
    </Layout>
  );
};

export default CostCategoriesPage;
